import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Button,
  Circle,
  Flex,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";

import { useGetConfigsQuery } from "redux/services/config";
import { useGetMenusQuery } from "redux/services/menu";
import type { Menu as MenuType } from "redux/services/menu/types";
import { setActiveMenu } from "redux/slices/menu";
import { useAppDispatch } from "redux/store";

import { ScrollingCarousel } from "@trendyol-js/react-carousel";
import slugify from "slugify";

import { encode } from "utils/hash";
import { generateTitle } from "utils/helpers";

type Props = {};
const MenuBar: React.FC<Props> = (): JSX.Element => {
  const { data = [] } = useGetMenusQuery();
  const mutatedData = [...data]
  const sortData = mutatedData?.sort((a, b) => a.sorted - b.sorted)
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: config } = useGetConfigsQuery();

  const handleNavigate =
    ({ id, url, source, isCctv }: any) =>
      () => {
        let uri = `${url}/${encode(source)}`;
        if (isCctv === 22) {
          uri = `apps/cctv/${url}`;
          if (url === "dashboard-surveillance/cctv-kominfo-sleman") {
            uri = `apps/cctv/dashboard-surveillance/cctv-kominfosleman`;
          }
        }
        navigate(uri);
        dispatch(
          setActiveMenu({
            id,
            url: uri,
            source,
          })
        );
      };
  const createMenu = (menu: MenuType) => {
    const parentMenu = slugify(menu.name, {
      lower: true,
    });

    if (menu.status) {
      const isParentSelected = params?.parent_menu === parentMenu;
      return (
        <Menu
          key={menu.id}
          isLazy
          autoSelect={false}
          gutter={25}
          placement="top-start"
        >
          <MenuButton
            mr="2"
            backgroundColor={isParentSelected ? "#fff" : "transparent"}
            color={isParentSelected ? "brand.500" : "#fff"}
            _hover={{
              backgroundColor: "#fff",
              color: "brand.500",
            }}
            _active={{
              backgroundColor: "#fff",
              color: "brand.500",
              borderColor: "#fff",
            }}
            _focus={{
              borderColor: "#fff",
            }}
            as={Button}
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              paddingTop: 30,
              paddingBottom: 30,
            }}
            maxW="min"
            textAlign="left"
            iconSpacing={4}
            lineHeight={1}
            role="group"
            textTransform="capitalize"
            // leftIcon={<EmailIcon w={6} h={6} />}
            leftIcon={
              <Circle
                size="40px"
                transition="all 250ms linear"
                bg={isParentSelected ? "brand.500" : "#fff"}
                color={isParentSelected ? "#fff" : "brand.500"}
                _active={{
                  bg: "brand.500",
                }}
                _focus={{
                  bg: "brand.500",
                }}
                _groupHover={{
                  bg: "brand.500",
                  color: "#fff",
                }}
              >
                {generateTitle(menu.name)}
              </Circle>
            }
          >
            {menu.name}
          </MenuButton>

          {menu.children.length > 0 && (
            <MenuList bgColor="brand.500" color="white" borderColor="brand.500">
              <MenuGroup>
                {menu.children
                  .filter((f) => f.status)
                  .map((childMenu) => {
                    const subMenu = slugify(childMenu.name, {
                      lower: true,
                    });
                    return (
                      <MenuItem
                        backgroundColor={
                          params?.menu === subMenu && isParentSelected
                            ? "#fff"
                            : "transparent"
                        }
                        color={
                          params?.menu === subMenu && isParentSelected
                            ? "brand.500"
                            : "#fff"
                        }
                        className="menu-item"
                        textTransform="capitalize"
                        // disabled={!!childMenu.source}
                        onClick={handleNavigate({
                          id: childMenu.id,
                          url: `${parentMenu}/${subMenu}`,
                          source: childMenu.source,
                          isCctv: childMenu.parent_id,
                        })}
                        key={childMenu.name}
                        transition="all 250ms linear"
                        _hover={{
                          color: "brand.500",
                          backgroundColor: "#white",
                        }}
                        icon={
                          <Circle
                            size="30px"
                            transition="all 250ms linear"
                            bg={
                              params?.menu === subMenu && isParentSelected
                                ? "brand.500"
                                : "#fff"
                            }
                            color={
                              params?.menu === subMenu && isParentSelected
                                ? "#fff"
                                : "brand.500"
                            }
                            border="transparent"
                            sx={{
                              ".menu-item:hover &": {
                                color: "#fff",
                                bgColor: "brand.500",
                              },
                            }}
                          >
                            {generateTitle(childMenu.name)}
                          </Circle>
                        }
                      >
                        {childMenu.name}
                      </MenuItem>
                    );
                  })}
              </MenuGroup>
            </MenuList>
          )}
        </Menu>
      );
    }
  };

  return (
    <Flex
      px="12"
      py="4"
      alignItems="center"
      position="fixed"
      bottom="0"
      right="0"
      left="0"
      bgColor="brand.500"
      zIndex="99"
    >
      <Text
        as="span"
        // maxW="14"
        fontSize="xl"
        fontWeight="bold"
        textAlign="center"
        color="#fff"
        lineHeight="none"
        mb={0}
        mr="5"
        onClick={() => {
          window.location.href = "/";
        }}
      >
        {config?.data[0]?.site_name}
      </Text>

      <ScrollingCarousel className="menu-slider">
        {sortData.map(createMenu)}
      </ScrollingCarousel>
    </Flex>
  );
};

export default MenuBar;
